/* Pivot: header. Mobile menu toggle (hamburger <-> X), animated submenu reveal,
   and the cart panel (open/close, remove items, stay in sync with the menu's cart count). */
if (!customElements.get('pivot-header')) {
  customElements.define(
    'pivot-header',
    class PivotHeader extends HTMLElement {
      connectedCallback() {
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.toggleButton = this.querySelector('[data-pivot-menu-toggle]');
        this.cartPanel = this.querySelector('[data-pivot-cart-panel]');

        // The mobile toggle closes the cart when it's open (it shows an X), otherwise toggles the menu
        this.toggleButton?.addEventListener('click', () => {
          if (this.classList.contains('is-cart-open')) return this.setCartOpen(false, true);
          this.setOpen(!this.classList.contains('is-open'));
        });
        this.querySelectorAll('[data-pivot-menu-close]').forEach((el) =>
          el.addEventListener('click', () => this.setOpen(false, el.tagName !== 'A'))
        );
        // A menu link to a section of the current page (e.g. /pages/info#privacy-policy): close the menu so the scroll is visible
        this.querySelectorAll('.pivot-menu a[href*="#"]').forEach((link) =>
          link.addEventListener('click', () => {
            if (link.pathname === window.location.pathname && link.hash) this.setOpen(false);
          })
        );
        this.querySelectorAll('[data-pivot-cart-open]').forEach((el) =>
          el.addEventListener('click', (event) => {
            if (!this.cartPanel) return;
            event.preventDefault();
            this.setCartOpen(!this.classList.contains('is-cart-open'));
          })
        );
        // Pop-ups (newsletter, search)
        this.querySelectorAll('[data-pivot-modal-open]').forEach((button) =>
          button.addEventListener('click', () => this.openModal(button.dataset.pivotModalOpen, button))
        );
        this.querySelectorAll('[data-pivot-modal-close]').forEach((el) =>
          el.addEventListener('click', () => this.closeModal())
        );
        // After a newsletter signup the page reloads; reopen the pop-up to show the thank-you / error
        // After a pop-up signup the page reloads; reopen the pop-up to show the thank-you / error.
        // Remembered per tab so a footer signup doesn't open the pop-up.
        const modalForm = this.querySelector('#PivotNewsletterModalForm');
        modalForm?.addEventListener('submit', () => {
          try {
            sessionStorage.setItem('pivot-newsletter-modal', '1');
          } catch (e) {}
        });
        let fromModal = window.location.hash === '#PivotNewsletterModalForm';
        try {
          fromModal = fromModal || sessionStorage.getItem('pivot-newsletter-modal') === '1';
          sessionStorage.removeItem('pivot-newsletter-modal');
        } catch (e) {}
        if (fromModal && this.querySelector('[data-pivot-newsletter-result]')) this.openModal('newsletter');

        this.addEventListener('keyup', (event) => {
          if (event.code !== 'Escape') return;
          if (this.openModalEl) this.closeModal();
          else if (this.classList.contains('is-cart-open')) this.setCartOpen(false, true);
          else if (this.classList.contains('is-open')) this.setOpen(false, true);
        });

        // Page-wide listeners (removed again in disconnectedCallback, so theme-editor re-renders don't stack them)
        this.onDocumentClick = (event) => {
          // Back links (product page, text pages, By Appointment): return to exactly where the visitor was when
          // they came from this store and there's history to go back to; otherwise the link's own URL
          const back = event.target.closest('[data-pivot-history-back]');
          if (back && document.referrer && history.length > 1) {
            if (new URL(document.referrer).origin === window.location.origin) {
              event.preventDefault();
              history.back();
            }
            return;
          }
          // Remove works in the panel and on the /cart page
          const remove = event.target.closest('[data-pivot-cart-remove]');
          if (remove) {
            event.preventDefault();
            this.removeItem(remove.dataset.pivotCartRemove, remove.href);
          }
        };
        this.onCartEvent = (event) => this.onCartUpdated(event.detail);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('pivot:cart-updated', this.onCartEvent);
        // Delegated: the cart contents are re-rendered after every change
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-pivot-cart-close]')) this.setCartOpen(false, true);
        });

        this.querySelectorAll('.pivot-menu__row--parent').forEach((details) => this.initSubmenu(details));

      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('pivot:cart-updated', this.onCartEvent);
      }

      setOpen(open, restoreFocus = false) {
        if (open) this.setCartOpen(false);
        this.classList.toggle('is-open', open);
        this.toggleButton?.setAttribute('aria-expanded', open);
        if (restoreFocus) this.toggleButton?.focus();
      }

      /* ---------- Pop-ups ---------- */

      openModal(name, trigger = null) {
        const modal = this.querySelector(`[data-pivot-modal="${name}"]`);
        if (!modal) return;
        this.setOpen(false);
        this.setCartOpen(false);
        this.closeModal(false);
        this.openModalEl = modal;
        this.modalTrigger = trigger;
        modal.classList.add('is-open');
        setTimeout(() => modal.querySelector('input:not([type="hidden"])')?.focus({ preventScroll: true }), 50);
      }

      closeModal(restoreFocus = true) {
        if (!this.openModalEl) return;
        this.openModalEl.classList.remove('is-open');
        this.openModalEl = null;
        if (restoreFocus) this.modalTrigger?.focus();
      }

      /* ---------- Cart panel ---------- */

      setCartOpen(open, restoreFocus = false) {
        if (!this.cartPanel) return;
        if (open) this.setOpen(false);
        this.classList.toggle('is-cart-open', open);
        this.querySelectorAll('[data-pivot-cart-open]').forEach((el) => el.setAttribute('aria-expanded', open));
        if (open) this.cartPanel.querySelector('button, a')?.focus({ preventScroll: true });
        else if (restoreFocus) this.querySelector('[data-pivot-cart-open]')?.focus();
      }

      async removeItem(key, fallbackUrl) {
        document.querySelectorAll('.pivot-cart').forEach((el) => el.classList.add('is-busy'));
        try {
          const response = await fetch(`${window.routes?.cart_change_url || '/cart/change'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
          if (!response.ok) throw new Error();
          document.dispatchEvent(new CustomEvent('pivot:cart-updated', { detail: await response.json() }));
        } catch (error) {
          // Shopify's own remove link: removes the item and returns to the cart page
          window.location.assign(fallbackUrl);
        }
      }

      /* Any cart change (add to cart, remove): refresh every copy of the cart and the menu count */
      async onCartUpdated(cart) {
        const count = cart.item_count;
        document.querySelectorAll('[data-pivot-cart-count]').forEach((el) => (el.textContent = `(${count})`));
        document.querySelectorAll('[data-pivot-cart]').forEach((el) => el.classList.toggle('is-filled', count > 0));

        try {
          const url = `${window.location.pathname}?section_id=${this.dataset.sectionId}`;
          const html = new DOMParser().parseFromString(await (await fetch(url)).text(), 'text/html');
          const fresh = html.querySelector('[data-pivot-cart-contents]');
          if (fresh) document.querySelectorAll('[data-pivot-cart-contents]').forEach((el) => (el.innerHTML = fresh.innerHTML));
        } finally {
          document.querySelectorAll('.pivot-cart').forEach((el) => el.classList.remove('is-busy'));
        }
      }

      /* ---------- Submenus: height grows from 0, revealing the list in place.
         Only one is open at a time: opening one closes any other. ---------- */

      initSubmenu(details) {
        const summary = details.querySelector('summary');
        const panel = details.querySelector('.pivot-menu__panel');
        if (!summary || !panel) return;
        this.submenus = this.submenus || [];
        this.submenus.push(details);

        details.addEventListener('toggle', () => {
          details.classList.toggle('is-expanded', details.open);
          // Reduced motion (no animation): the browser opens it directly, so close the others here
          if (details.open && this.reduceMotion.matches) {
            this.submenus.forEach((other) => {
              if (other !== details && other.open) other.open = false;
            });
          }
        });

        summary.addEventListener('click', (event) => {
          if (this.reduceMotion.matches) return;
          event.preventDefault();
          const opening = !details.classList.contains('is-expanded');
          if (opening) {
            this.submenus.forEach((other) => {
              if (other !== details && other.classList.contains('is-expanded')) this.animateSubmenu(other, false);
            });
          }
          this.animateSubmenu(details, opening);
        });
      }

      animateSubmenu(details, opening) {
        const panel = details.querySelector('.pivot-menu__panel');
        // Measured before opening, so a closed panel starts at 0; an interrupted one starts where it is
        const from = details.open ? panel.getBoundingClientRect().height : 0;
        details.animation?.cancel();
        details.classList.toggle('is-expanded', opening);
        if (opening) details.open = true;
        const to = opening ? panel.scrollHeight : 0;

        details.animation = panel.animate(
          { height: [`${from}px`, `${to}px`] },
          { duration: 320, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
        );
        details.animation.onfinish = () => {
          details.animation = null;
          if (!opening) details.open = false;
        };
      }
    }
  );
}
